export const SOCIAL_RETURN_TO_KEY = 'tee-time.oauth.returnTo';
export const SOCIAL_REMEMBER_KEY = 'tee-time.oauth.remember';
export const SOCIAL_PENDING_PROVIDER_KEY = 'tee-time.oauth.pendingProvider';
export const SOCIAL_PENDING_TARGET_KEY = 'tee-time.oauth.pendingTarget';
export const SOCIAL_PENDING_TIMESTAMP_KEY = 'tee-time.oauth.pendingTimestamp';
export const SOCIAL_POPUP_FLOW_KEY = 'tee-time.oauth.popupFlow';
export const SOCIAL_POPUP_FLOW_ID_KEY = 'tee-time.oauth.popupFlowId';
/**
 * Booking-scoped "social account not linked" marker. Set when a social attempt
 * that ORIGINATED FROM the booking gate returns `error=not-linked` after a
 * full-page OAuth redirect (the popup path carries the signal in-memory). The
 * marker survives the redirect so the gate can re-render the not-linked card
 * once `CheckoutResumeManager` reopens checkout. It is only ever set/read while
 * a checkout-resume marker is present, so the plain `/login` page is unaffected.
 */
export const SOCIAL_NOT_LINKED_KEY = 'tee-time.oauth.notLinked';
export const SOCIAL_LOGIN_POPUP_MESSAGE_TYPE = 'tee-time.oauth.popup.complete';
export const SOCIAL_LOGIN_POPUP_CHANNEL_NAME = 'tee-time.oauth.popup';
export const SOCIAL_LOGIN_POPUP_STORAGE_KEY = 'tee-time.oauth.popupMessage';
export const SOCIAL_LINK_PENDING_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
export const TEE_TIME_WEB_OAUTH_AUDIENCE = 'teetime-web';
const SOCIAL_PROVIDER_LABELS = {
    google: 'Google',
    facebook: 'Facebook',
    instagram: 'Instagram',
};
export function isSocialProviderId(provider) {
    return provider === 'google' || provider === 'facebook' || provider === 'instagram';
}
export function parseSocialProviderId(provider) {
    const normalized = provider?.trim().toLowerCase();
    return isSocialProviderId(normalized) ? normalized : null;
}
export const SOCIAL_LOGIN_PROVIDERS = [
    { id: 'google', label: `Login with ${SOCIAL_PROVIDER_LABELS.google}` },
    { id: 'facebook', label: `Login with ${SOCIAL_PROVIDER_LABELS.facebook}` },
    { id: 'instagram', label: `Login with ${SOCIAL_PROVIDER_LABELS.instagram}` },
];
export function getProviderLabel(provider) {
    const providerId = parseSocialProviderId(provider);
    return providerId ? SOCIAL_PROVIDER_LABELS[providerId] : 'Social provider';
}
export function getIdpBaseUrl() {
    const im = globalThis
        .importMetaEnv;
    const env = globalThis
        .process?.env;
    return im?.['VITE_IDP_BASE_URL'] ?? env?.['VITE_IDP_BASE_URL'] ?? '';
}
export function resolveBaseUrl(pathname) {
    const baseRaw = getIdpBaseUrl();
    let resolvedBase;
    try {
        resolvedBase = baseRaw
            ? new URL(baseRaw, window.location.origin).toString()
            : window.location.origin;
    }
    catch {
        resolvedBase = window.location.origin;
    }
    const target = new URL(pathname, resolvedBase);
    return target.toString();
}
export function computeLoginReturnBase() {
    const { origin, pathname } = window.location;
    const basePath = pathname.replace(/\/login\/?$/, '');
    const joined = `${origin}${basePath}`;
    return joined;
}
function requireSocialProviderId(provider) {
    const providerId = parseSocialProviderId(provider);
    if (!providerId) {
        throw new Error('Unsupported social provider');
    }
    return providerId;
}
function setOptionalStringParam(url, key, value) {
    const trimmed = value?.trim();
    if (trimmed) {
        url.searchParams.set(key, trimmed);
    }
}
export function buildAuthUrl(provider, options = {}) {
    const url = new URL(resolveBaseUrl(`/api/auth/${requireSocialProviderId(provider)}`));
    setOptionalStringParam(url, 'audience', options.audience);
    return url.toString();
}
/**
 * Build the provider OAuth URL for a checkout social-login redirect.
 *
 * The IDP appends `/login` to the `returnTo` query before redirecting back.
 * Only the flat `/login` route is defined in the SPA, so the `returnTo` MUST be
 * origin-only — otherwise a non-root listing (e.g. `/club/:code/teetimes`)
 * resolves to a non-existent `/club/:code/teetimes/login` route (404) and the
 * post-OAuth booking resume is stranded. The relative listing path is preserved
 * separately via `persistSocialLoginContext` for the in-app navigation that
 * runs after the token is consumed on `/login`.
 */
export function buildCheckoutSocialLoginUrl(provider, origin) {
    const url = new URL(buildAuthUrl(provider));
    url.searchParams.set('returnTo', origin);
    return url.toString();
}
export function buildCheckoutSocialPopupStartUrl(provider, returnTo, flowId, options = {}) {
    const providerId = requireSocialProviderId(provider);
    const url = new URL('/login', window.location.origin);
    url.searchParams.set('oauthPopup', '1');
    url.searchParams.set('provider', providerId);
    url.searchParams.set('flowId', flowId);
    if (options.mode === 'signup') {
        url.searchParams.set('mode', 'signup');
    }
    if (returnTo?.startsWith('/')) {
        url.searchParams.set('returnTo', returnTo);
    }
    return url.toString();
}
export function buildLinkUrl(provider) {
    return resolveBaseUrl(`/api/auth/${requireSocialProviderId(provider)}/link`);
}
export function buildSignupUrl(provider, options = {}) {
    const url = new URL(resolveBaseUrl(`/api/auth/${requireSocialProviderId(provider)}/signup`));
    setOptionalStringParam(url, 'profile', options.profile);
    return url.toString();
}
export function computeLinkReturnBase() {
    const { origin } = window.location;
    return `${origin}/auth/social-link`;
}
export function computeSignupReturnBase() {
    const { origin, pathname } = window.location;
    // Remove /login suffix to get base path for signup callback
    return origin + pathname.replace(/\/login\/?$/, '');
}
export function persistSocialLoginContext(returnTo, remember) {
    if (typeof window === 'undefined')
        return;
    try {
        sessionStorage.setItem(SOCIAL_REMEMBER_KEY, remember ? 'true' : 'false');
    }
    catch {
        /* ignore storage errors */
    }
    try {
        if (returnTo && returnTo.startsWith('/')) {
            sessionStorage.setItem(SOCIAL_RETURN_TO_KEY, returnTo);
        }
        else {
            sessionStorage.removeItem(SOCIAL_RETURN_TO_KEY);
        }
    }
    catch {
        /* ignore storage errors */
    }
}
export function storePendingProvider(provider, target) {
    if (typeof window === 'undefined')
        return;
    const providerId = parseSocialProviderId(provider);
    if (!providerId)
        return;
    try {
        sessionStorage.setItem(SOCIAL_PENDING_PROVIDER_KEY, providerId);
        sessionStorage.setItem(SOCIAL_PENDING_TIMESTAMP_KEY, Date.now().toString());
    }
    catch {
        /* ignore storage errors */
    }
    try {
        if (target && target.startsWith('/')) {
            sessionStorage.setItem(SOCIAL_PENDING_TARGET_KEY, target);
        }
        else if (!sessionStorage.getItem(SOCIAL_PENDING_TARGET_KEY)) {
            sessionStorage.setItem(SOCIAL_PENDING_TARGET_KEY, '/');
        }
    }
    catch {
        /* ignore */
    }
}
export function createSocialLoginPopupFlowId() {
    try {
        return window.crypto.randomUUID();
    }
    catch {
        return `oauth-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
}
export function markSocialLoginPopupFlow(provider, flowId) {
    if (typeof window === 'undefined')
        return;
    const providerId = parseSocialProviderId(provider);
    const trimmedFlowId = flowId.trim();
    if (!providerId || !trimmedFlowId)
        return;
    try {
        sessionStorage.setItem(SOCIAL_POPUP_FLOW_KEY, providerId);
        sessionStorage.setItem(SOCIAL_POPUP_FLOW_ID_KEY, trimmedFlowId);
    }
    catch {
        /* ignore storage errors */
    }
}
export function readSocialLoginPopupFlow() {
    if (typeof window === 'undefined')
        return null;
    try {
        const provider = parseSocialProviderId(sessionStorage.getItem(SOCIAL_POPUP_FLOW_KEY));
        const flowId = sessionStorage.getItem(SOCIAL_POPUP_FLOW_ID_KEY)?.trim() ?? '';
        return provider && flowId ? { provider, flowId } : null;
    }
    catch {
        return null;
    }
}
export function clearSocialLoginPopupFlow() {
    if (typeof window === 'undefined')
        return;
    try {
        sessionStorage.removeItem(SOCIAL_POPUP_FLOW_KEY);
        sessionStorage.removeItem(SOCIAL_POPUP_FLOW_ID_KEY);
    }
    catch {
        /* ignore storage errors */
    }
}
export function parseSocialLoginPopupMessage(value) {
    const candidate = typeof value === 'string'
        ? (() => {
            try {
                return JSON.parse(value);
            }
            catch {
                return null;
            }
        })()
        : value;
    if (!candidate || typeof candidate !== 'object')
        return null;
    const data = candidate;
    const provider = parseSocialProviderId(data.provider);
    const flowId = typeof data.flowId === 'string' ? data.flowId.trim() : '';
    if (data.type !== SOCIAL_LOGIN_POPUP_MESSAGE_TYPE ||
        !flowId ||
        (data.status !== 'started' && data.status !== 'complete')) {
        return null;
    }
    return {
        type: SOCIAL_LOGIN_POPUP_MESSAGE_TYPE,
        flowId,
        status: data.status,
        ok: data.ok === true,
        provider,
        ...(data.mode === 'login' || data.mode === 'signup' ? { mode: data.mode } : {}),
        ...(typeof data.target === 'string' ? { target: data.target } : {}),
        ...(data.reason === 'not-linked' ||
            data.reason === 'oauth-failed' ||
            data.reason === 'mfa-required' ||
            data.reason === 'signup-failed'
            ? { reason: data.reason }
            : {}),
        ...(typeof data.error === 'string' ? { error: data.error } : {}),
    };
}
export function notifySocialLoginPopup(message) {
    if (typeof window === 'undefined')
        return;
    const payload = {
        type: SOCIAL_LOGIN_POPUP_MESSAGE_TYPE,
        ...message,
    };
    try {
        const channel = new BroadcastChannel(SOCIAL_LOGIN_POPUP_CHANNEL_NAME);
        channel.postMessage(payload);
        channel.close();
    }
    catch {
        /* ignore broadcast errors */
    }
    try {
        const storagePayload = JSON.stringify({ ...payload, deliveredAt: Date.now() });
        window.localStorage.setItem(SOCIAL_LOGIN_POPUP_STORAGE_KEY, storagePayload);
        window.localStorage.removeItem(SOCIAL_LOGIN_POPUP_STORAGE_KEY);
    }
    catch {
        /* ignore storage delivery errors */
    }
}
export function readPendingProvider() {
    if (typeof window === 'undefined')
        return null;
    try {
        const stored = parseSocialProviderId(sessionStorage.getItem(SOCIAL_PENDING_PROVIDER_KEY));
        if (!stored) {
            return null;
        }
        const timestampRaw = sessionStorage.getItem(SOCIAL_PENDING_TIMESTAMP_KEY);
        if (!timestampRaw) {
            return stored;
        }
        const storedAt = Number.parseInt(timestampRaw, 10);
        if (Number.isNaN(storedAt)) {
            sessionStorage.removeItem(SOCIAL_PENDING_TIMESTAMP_KEY);
            return stored;
        }
        if (Date.now() - storedAt <= SOCIAL_LINK_PENDING_MAX_AGE_MS) {
            return stored;
        }
        clearPendingSocialState();
        return null;
    }
    catch {
        /* ignore */
    }
    return null;
}
export function readPendingTarget() {
    if (typeof window === 'undefined')
        return null;
    try {
        const value = sessionStorage.getItem(SOCIAL_PENDING_TARGET_KEY);
        return value && value.startsWith('/') ? value : null;
    }
    catch {
        return null;
    }
}
export function clearPendingSocialState() {
    if (typeof window === 'undefined')
        return;
    try {
        sessionStorage.removeItem(SOCIAL_PENDING_PROVIDER_KEY);
        sessionStorage.removeItem(SOCIAL_PENDING_TARGET_KEY);
        sessionStorage.removeItem(SOCIAL_RETURN_TO_KEY);
        sessionStorage.removeItem(SOCIAL_REMEMBER_KEY);
        sessionStorage.removeItem(SOCIAL_PENDING_TIMESTAMP_KEY);
    }
    catch {
        /* ignore */
    }
}
/** Maximum age of a booking not-linked marker before it is considered stale. */
export const SOCIAL_NOT_LINKED_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
/**
 * Persist the booking-scoped not-linked marker so it survives the full-page
 * OAuth redirect back into the booking listing. Caller is responsible for only
 * invoking this when the attempt originated from the booking gate (i.e. a
 * checkout-resume marker is present).
 */
export function markSocialNotLinked(provider) {
    if (typeof window === 'undefined')
        return;
    const providerId = parseSocialProviderId(provider);
    if (!providerId)
        return;
    try {
        const payload = { provider: providerId, at: Date.now() };
        sessionStorage.setItem(SOCIAL_NOT_LINKED_KEY, JSON.stringify(payload));
    }
    catch {
        /* ignore storage errors */
    }
}
/**
 * Read the booking-scoped not-linked marker. Stale, malformed, or missing
 * markers are cleared and `null` is returned. Reading does NOT consume the
 * marker — the gate clears it explicitly on dismiss / email-switch / success.
 */
export function readSocialNotLinked() {
    if (typeof window === 'undefined')
        return null;
    try {
        const raw = sessionStorage.getItem(SOCIAL_NOT_LINKED_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        const provider = parseSocialProviderId(parsed?.provider);
        const at = typeof parsed?.at === 'number' ? parsed.at : Number.NaN;
        if (!provider || Number.isNaN(at) || Date.now() - at > SOCIAL_NOT_LINKED_MAX_AGE_MS) {
            clearSocialNotLinked();
            return null;
        }
        return { provider, at };
    }
    catch {
        clearSocialNotLinked();
        return null;
    }
}
/** Remove any stored booking not-linked marker. */
export function clearSocialNotLinked() {
    if (typeof window === 'undefined')
        return;
    try {
        sessionStorage.removeItem(SOCIAL_NOT_LINKED_KEY);
    }
    catch {
        /* ignore storage errors */
    }
}
export function consumeSocialLoginContext(defaultRemember) {
    if (typeof window === 'undefined') {
        return { remember: defaultRemember, returnTo: null };
    }
    let remember = defaultRemember;
    let returnTo = null;
    try {
        const storedRemember = sessionStorage.getItem(SOCIAL_REMEMBER_KEY);
        if (storedRemember === 'true')
            remember = true;
        else if (storedRemember === 'false')
            remember = false;
    }
    catch {
        /* ignore */
    }
    try {
        const storedReturn = sessionStorage.getItem(SOCIAL_RETURN_TO_KEY);
        if (storedReturn?.startsWith('/')) {
            returnTo = storedReturn;
        }
    }
    catch {
        /* ignore */
    }
    try {
        sessionStorage.removeItem(SOCIAL_REMEMBER_KEY);
        sessionStorage.removeItem(SOCIAL_RETURN_TO_KEY);
    }
    catch {
        /* ignore */
    }
    return { remember, returnTo };
}
//# sourceMappingURL=socialAuth.js.map